import { apiDelete, apiGet, apiPatch, apiPost } from './utils';
import type { EmailAddress, FHRequest, FHRequestMessage, PagedResponse } from '../types';

export interface ManagerUser {
  uuid: string;
  key: string;
  first_name: string | null;
  last_name: string | null;
  primary_email: string;
  is_disabled: boolean;
  is_beta: boolean;
  is_ic: boolean;
  roles: string[];
  has_auto_mentor: boolean;
  is_mentor: boolean;
  has_manager: boolean;
  has_sudo: boolean;
  // Claim limits (assistants). 0 = default cap.
  allowed_open_tasks?: number;
  date_next_claim?: string | null;
  // Subscription fields (for clients)
  subscription_service?: string | null;
  subscription_product?: string | null;
  next_billing_cycle_date?: string | null;
  credit_included?: number;
  current_credit?: number;
  tasks_remaining?: number;
  // Billing provider IDs
  stripe_customer_id?: string | null;
  recurly_account_code?: string | null;
  // primary first, then verified alternates, then pending ones
  emails?: EmailAddress[];
}

export interface ManagerUserUpdate {
  first_name?: string | null;
  last_name?: string | null;
  primary_email?: string;
  is_disabled?: boolean;
  is_beta?: boolean;
  is_ic?: boolean;
  auto_mentor?: boolean;
  is_mentor?: boolean;
  is_manager?: boolean;
  allowed_open_tasks?: number;
  clear_date_next_claim?: boolean;
}

// Assistants a client has asked never to be matched with
export interface BannedAssistant {
  key: string;
  reason: string;
  date_created: string | null;
  assistant_name: string | null;
  assistant_email: string | null;
}

export interface AssistantOption {
  key: string;
  name: string | null;
  email: string;
}

export interface PricingUpdate {
  credits?: number;
  price?: number;
  paid?: boolean;
}

export interface ManagerDwollaSource {
  id: string;
  name: string;
  type: string;
  bank_account_type: string;
  bank_name: string | null;
  created: string;
}

export interface ManagerDwollaTransaction {
  id: string;
  status: string;
  amount: string;
  currency: string;
  created: string;
}

export interface ManagerDwollaInfo {
  user_uuid: string;
  dwolla_customer_location: string | null;
  dwolla_funding_location: string | null;
  has_customer: boolean;
  has_funding_source: boolean;
  sources: ManagerDwollaSource[];
  transactions: ManagerDwollaTransaction[];
}

export interface ManagerSubscription {
  key: string;
  uuid: string;
  plan_name: string;
  service: string;
  service_subscription_status: string;
  date_updated: string;
}

export interface RatingItem {
  uuid: string;
  key: string;
  user: string;
  assistant: string;
  request: string;
  rating: number;
  date_created: string;
  client_name: string | null;
  assistant_name: string | null;
  request_title: string | null;
  request_key: string | null;
}

export const managerApi = {
  /**
   * Search for a user by email address
   */
  async searchByEmail(email: string): Promise<ManagerUser> {
    return apiGet<ManagerUser>('/users/manager/search', { email });
  },

  /**
   * Get full user info for manager editing
   */
  async getUser(userKey: string): Promise<ManagerUser> {
    return apiGet<ManagerUser>(`/users/manager/${userKey}`);
  },

  /**
   * Update user info and roles (manager only)
   */
  async updateUser(userKey: string, data: ManagerUserUpdate): Promise<ManagerUser> {
    return apiPatch<ManagerUser>(`/users/manager/${userKey}`, data);
  },

  async getBannedAssistants(userKey: string): Promise<BannedAssistant[]> {
    return apiGet<BannedAssistant[]>(`/users/manager/${userKey}/banned-assistants`);
  },

  async banAssistant(userKey: string, assistantEmail: string, reason: string): Promise<BannedAssistant> {
    return apiPost<BannedAssistant>(`/users/manager/${userKey}/banned-assistants`, {
      assistant_email: assistantEmail,
      reason,
    });
  },

  async unbanAssistant(userKey: string, banKey: string): Promise<{ deleted: string }> {
    return apiDelete<{ deleted: string }>(`/users/manager/${userKey}/banned-assistants/${banKey}`);
  },

  /**
   * Unassign a request (remove claimed_by)
   */
  async unassignRequest(requestKey: string): Promise<FHRequest> {
    return apiPost<FHRequest>('/request/support-assign', {
      request: requestKey,
      assign_user: 'unassigned',
    });
  },

  /**
   * Update request pricing (price, credits, paid status)
   */
  async updateRequestPricing(
    requestKey: string,
    data: PricingUpdate
  ): Promise<FHRequest> {
    return apiPost<FHRequest>('/request/support/update-price', {
      request: requestKey,
      ...data,
    });
  },

  /**
   * Get available assistants for reassignment
   */
  async getAvailableAssistants(): Promise<{ items: AssistantOption[] }> {
    return apiGet<{ items: AssistantOption[] }>('/request/support/available-assistants', {
    });
  },

  /**
   * Reassign request to a different user
   */
  async reassignRequest(
    requestKey: string,
    userKey: string,
  ): Promise<FHRequest> {
    return apiPost<FHRequest>('/request/support-assign', {
      request: requestKey,
      assign_user: userKey,
    });
  },

  /**
   * Grant a role to a user (sudo only)
   */
  async grantRole(userKey: string, role: string): Promise<{ role: string; user: string }[]> {
    return apiPost<{ role: string; user: string }[]>('/role/grant', {
      user: userKey,
      role: role,
    });
  },

  /**
   * Revoke a role from a user (sudo only)
   */
  async revokeRole(userKey: string, role: string): Promise<{ role: string; user: string }[]> {
    return apiPost<{ role: string; user: string }[]>('/role/revoke', {
      user: userKey,
      role: role,
    });
  },

  /**
   * Get all requests (open + closed) for a specific assistant
   * Requires manager role
   */
  async getAssistantRequests(
    userKey: string,
    cursor?: string
  ): Promise<{ items: FHRequest[]; cursor: string | null }> {
    return apiGet<{ items: FHRequest[]; cursor: string | null }>('/request/list/all/by-manager', {
      user: userKey,
      ...(cursor && { cursor }),
    });
  },

  /**
   * Get closed requests for a specific assistant
   * Requires manager role
   */
  async getAssistantClosedRequests(
    userKey: string,
    cursor?: string
  ): Promise<{ items: FHRequest[]; cursor: string | null }> {
    return apiGet<{ items: FHRequest[]; cursor: string | null }>('/request/list/closed/by-manager', {
      user: userKey,
      ...(cursor && { cursor }),
    });
  },

  /**
   * Get all requests owned by a specific client
   * Requires manager role
   */
  async getClientRequests(
    userKey: string,
    cursor?: string
  ): Promise<{ items: FHRequest[]; cursor: string | null }> {
    return apiGet<{ items: FHRequest[]; cursor: string | null }>('/request/list/client/by-manager', {
      user: userKey,
      ...(cursor && { cursor }),
    });
  },

  // Manager Queue API

  /**
   * Get requests needing manager attention
   * Requires manager role
   */
  async getQueue(cursor?: string): Promise<PagedResponse<FHRequest>> {
    return apiGet<PagedResponse<FHRequest>>('/request/list/manager-attention', {
      ...(cursor && { cursor }),
    });
  },

  /**
   * Send a note to the assistant and resolve the attention flag
   * Requires manager role
   * Optionally updates the request status
   */
  async respondToRequest(
    requestKey: string,
    message: string,
    status?: number
  ): Promise<{ request: FHRequest; message: FHRequestMessage }> {
    return apiPost<{ request: FHRequest; message: FHRequestMessage }>('/request/manager/respond', {
      request: requestKey,
      message,
      ...(status !== undefined && { status }),
    });
  },

  /**
   * Close request without notifying the client
   * Requires manager role
   */
  async closeQuietly(requestKey: string): Promise<FHRequest> {
    return apiPost<FHRequest>('/request/manager/close-quietly', {
      request: requestKey,
    });
  },

  /**
   * Clear attention flag without sending a message
   * Requires manager role
   */
  async resolveAttention(requestKey: string): Promise<FHRequest> {
    return apiPost<FHRequest>('/request/manager/resolve', {
      request: requestKey,
    });
  },

  /**
   * Get Dwolla payout info for an assistant (manager view - read only)
   * Requires manager role
   */
  async getDwollaInfo(userKey: string): Promise<ManagerDwollaInfo> {
    return apiGet<ManagerDwollaInfo>(`/dwolla/manager/${userKey}`);
  },

  /**
   * Get subscription records for a user
   * Requires support role
   */
  async getSubscriptions(
    userKey: string,
  ): Promise<{ items: ManagerSubscription[]; cursor: string | null }> {
    return apiGet<{ items: ManagerSubscription[]; cursor: string | null }>(
      '/subscription/by-user-via-manager',
      { user_key: userKey }
    );
  },

  /**
   * Set or clear the needs_manager_attention flag
   * Requires manager role
   */
  async setManagerAttention(
    requestKey: string,
    needsAttention: boolean
  ): Promise<FHRequest> {
    return apiPost<FHRequest>('/request/manager/set-attention', {
      request: requestKey,
      needs_attention: needsAttention,
    });
  },

  /**
   * Archive a request
   * Requires manager role
   */
  async archiveRequest(requestKey: string): Promise<FHRequest> {
    return apiPost<FHRequest>('/request/manager/archive', {
      request: requestKey,
    });
  },

  /**
   * Sync subscriptions and payments from Stripe
   * Requires support role
   */
  async syncStripeAccount(
    stripeCustomerId: string,
  ): Promise<{ subscriptions: ManagerSubscription[]; payments: unknown[] }> {
    return apiPost('/subscription/sync-via-shift', {
      stripe_customer_id: stripeCustomerId,
    });
  },

  /**
   * Sync subscriptions and payments from Recurly
   * Requires support role
   */
  async syncRecurlyAccount(
    recurlyAccountCode: string,
  ): Promise<{ subscriptions: ManagerSubscription[]; payments: unknown[] }> {
    return apiPost('/subscription/sync-recurly-via-shift', {
      recurly_account_code: recurlyAccountCode,
    });
  },

  /**
   * Set or update the internal manager note on a request
   * Requires manager role
   */
  async updateManagerNote(
    requestKey: string,
    godNote: string
  ): Promise<FHRequest> {
    return apiPost<FHRequest>('/request/manager/note', {
      request: requestKey,
      god_note: godNote,
    });
  },

  /**
   * Get recent client ratings (manager view)
   * Requires manager role
   */
  async getRecentRatings(
    cursor?: string
  ): Promise<{ items: RatingItem[]; cursor: string | null }> {
    return apiGet<{ items: RatingItem[]; cursor: string | null }>('/request/ratings/recent', {
      ...(cursor && { cursor }),
    });
  },
};
